import React, { useEffect, useState } from "react";
import { usStart, usStop, usStatus, usCloseSignal, usLessons, usTradesPdfUrl, usLearning, usLiveMarks, usNews } from "@/lib/api";
import { toast } from "sonner";
import {
  Power, Loader2, X, Clock, Activity, Target, AlertTriangle, RefreshCw, Download,
  Brain, ArrowUpRight, ArrowDownRight, Zap, Newspaper, CheckCircle2, ShieldX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LiveClock from "@/components/LiveClock";
import LearningPanel from "@/components/LearningPanel";

const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10, 15, 20];
const ACCENT = "#F472B6";
const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function Part2USIntraday() {
  const [config, setConfig] = useState({ capital: "25000", riskPercent: "1", leverage: 5 });
  const [status, setStatus] = useState(null);
  const [lessons, setLessons] = useState({ lessons: [], count: 0 });
  const [marks, setMarks] = useState({});
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const reload = async () => {
    try {
      const [s, l] = await Promise.all([usStatus(), usLessons().catch(() => ({ lessons: [], count: 0 }))]);
      setStatus(s);
      setLessons(l);
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); const iv = setInterval(reload, 30000); return () => clearInterval(iv); }, []);

  // News (refresh every 5 min)
  useEffect(() => {
    let m = true;
    const load = async () => { try { const d = await usNews(); if (m) setNews(d); } catch { /* */ } };
    load(); const iv = setInterval(load, 5 * 60 * 1000);
    return () => { m = false; clearInterval(iv); };
  }, []);

  // Live marks — poll every 3s while positions are open
  useEffect(() => {
    let m = true;
    const load = async () => {
      try {
        const d = await usLiveMarks();
        if (!m) return;
        const map = {}; (d.marks || []).forEach(x => { map[x.signal_id] = x; });
        setMarks(map);
      } catch { /* */ }
    };
    load(); const iv = setInterval(load, 3000);
    return () => { m = false; clearInterval(iv); };
  }, [status?.active_signals?.length]);

  const bot = status?.bot;
  const active = bot?.active;
  const marketOpen = !!status?.market_open;
  const activeSignals = status?.active_signals || [];
  const closedSignals = (status?.recent_signals || []).filter(s => s.status === "CLOSED").slice(0, 10);
  const totalRealized = closedSignals.reduce((a, s) => a + (Number(s.realized_pnl) || 0), 0);
  const totalUnrealized = Object.values(marks).reduce((a, m) => a + (Number(m.unrealized_pnl) || 0), 0);
  const watch = bot?.watchlist || [];
  const portfolio = status?.latest_status;

  const start = async () => {
    const cap = parseFloat(config.capital);
    const r = parseFloat(config.riskPercent);
    const lev = Number(config.leverage);
    if (!cap || cap < 500) return toast.error("Capital must be >= $500");
    if (!r || r <= 0 || r > 25) return toast.error("Risk must be (0, 25]");
    if (!lev || lev < 1 || lev > 20) return toast.error("Leverage must be 1x-20x");
    setWorking(true);
    try {
      await usStart({ capital: cap, risk_percent: r, leverage: lev, currency: "USD" });
      toast.success("NAVA-US online");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
    finally { setWorking(false); }
  };
  const stop = async () => {
    setWorking(true);
    try { await usStop(); toast.success("US engine stopped"); reload(); }
    catch { toast.error("Stop failed"); }
    finally { setWorking(false); }
  };
  const closeSig = async (id) => {
    if (!confirm("Close this position at current market price?")) return;
    try { await usCloseSignal(id); toast.success("Position closed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };
  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("tex_session_token");
      const r = await fetch(usTradesPdfUrl(), { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `nava-us-trades-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F472B6]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="us-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-zinc-900 bg-gradient-to-br from-[#0e0714] via-[#0A0A0A] to-black p-6 md:p-8">
        <div className="absolute inset-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,#F472B620,transparent_60%)]" />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#F472B6] flex items-center gap-2">
              <Zap className="w-3 h-3" /> Part 2 · US Intraday Engine
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold mt-2 leading-tight">NAVA-US</h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-xl">
              Same-day US stocks & ETF intraday scanner. AI runs a scanner → liquidity → volatility → news → prediction → risk pipeline, tracks live P&L, and auto square-off at 3:55 PM ET. Adaptive learning gets sharper every trade.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border ${marketOpen ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-zinc-800/50 border-zinc-800 text-zinc-500"}`}>
              {marketOpen ? "US Market Open" : "US Market Closed"}
            </span>
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border ${active ? "bg-[#F472B6]/10 border-[#F472B6]/30 text-[#F472B6]" : "bg-zinc-800/50 border-zinc-800 text-zinc-500"}`}>
              {active ? "Bot Active" : "Bot Idle"}
            </span>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Market Clocks</span>
          <LiveClock zones={["US", "IN"]} compact />
        </div>
      </section>

      {/* Disclaimer */}
      <div className="rounded-xl border border-[#F472B6]/20 bg-[#F472B6]/5 px-4 py-3 text-xs text-zinc-300 flex gap-3">
        <AlertTriangle className="w-4 h-4 text-[#F472B6] shrink-0 mt-0.5" />
        <div>
          US intraday signals sized in whole shares (notional mode). Live prices via Yahoo Finance. Runs only during US market hours (09:30–16:00 ET) with auto square-off at 3:55 PM ET. <strong>Assistive analysis — not financial advice.</strong>
        </div>
      </div>

      {/* Config / Start */}
      {!active ? (
        <section className="rounded-2xl border border-zinc-900 bg-zinc-950/50 p-5" data-testid="us-config-panel">
          <div className="text-sm font-semibold text-white mb-4">Activate NAVA-US</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Capital ($)</div>
              <input data-testid="us-cap-input" value={config.capital}
                onChange={(e) => setConfig({ ...config, capital: e.target.value.replace(/[^\d]/g, "") })}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:border-[#F472B6]/40 outline-none" />
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Risk %</div>
              <input data-testid="us-risk-input" value={config.riskPercent}
                onChange={(e) => setConfig({ ...config, riskPercent: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:border-[#F472B6]/40 outline-none" />
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Leverage</div>
              <select data-testid="us-lev-select" value={config.leverage}
                onChange={(e) => setConfig({ ...config, leverage: Number(e.target.value) })}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:border-[#F472B6]/40 outline-none">
                {LEVERAGE_OPTIONS.map(v => <option key={v} value={v}>{v}x</option>)}
              </select>
            </label>
          </div>
          <button data-testid="us-start-btn" onClick={start} disabled={working}
            className="mt-5 w-full sm:w-auto px-6 py-2.5 rounded-lg bg-[#F472B6] text-black font-semibold flex items-center gap-2 hover:brightness-110 disabled:opacity-50">
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            Start US Engine
          </button>
        </section>
      ) : (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="us-summary">
          <StatCard label="Capital" value={usd(bot.capital)} />
          <StatCard label="Leverage" value={`${bot.leverage}x`} />
          <StatCard label="Realized P&L" value={usd(totalRealized)} tone={totalRealized > 0 ? "up" : totalRealized < 0 ? "down" : "flat"} />
          <StatCard label="Active" value={`${activeSignals.length} / 2`} />
          <div className="col-span-2 md:col-span-1 flex items-center gap-2">
            <button data-testid="us-refresh-btn" onClick={reload} className="flex-1 px-3 py-2 rounded-lg border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs text-zinc-300 flex items-center gap-2 justify-center">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button data-testid="us-stop-btn" onClick={stop} disabled={working} className="flex-1 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-xs text-red-400">
              Stop
            </button>
          </div>
        </section>
      )}

      {/* News / Event Filter */}
      <section className="rounded-xl border border-zinc-900 bg-zinc-950/40 p-4" data-testid="us-news-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Newspaper className="w-3 h-3" /> News / Event Filter
          </div>
          {news?.available && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#F472B6]/30 bg-[#F472B6]/10 text-[#F472B6]">
              Sentiment: {news.overall_sentiment}
            </span>
          )}
        </div>
        {!news?.available ? (
          <div className="text-xs text-zinc-500">Fetching live market news…</div>
        ) : (
          <>
            {news.market_read && (
              <div className="mb-2 rounded-lg border border-[#F472B6]/20 bg-[#F472B6]/5 px-3 py-2 text-xs text-zinc-200">
                <span className="font-mono uppercase text-[10px] tracking-widest text-[#F472B6] mr-1">AI Read</span>
                {news.market_read}
              </div>
            )}
            {(news.movers || []).length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {news.movers.slice(0, 8).map((m, i) => (
                  <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${m.bias === "up" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"}`}>
                    {m.ticker} {m.bias === "up" ? "▲" : "▼"} <span className="text-zinc-500">{m.why}</span>
                  </span>
                ))}
              </div>
            )}
            {(news.headlines || []).length === 0 ? (
              <div className="text-xs text-zinc-500">No fresh market headlines this cycle.</div>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {news.headlines.slice(0, 8).map((h, i) => (
                  <div key={i} className="text-xs flex items-start gap-2">
                    <span className={`mt-0.5 shrink-0 font-mono text-[9px] px-1.5 py-0.5 rounded ${h.sentiment.includes("Bull") ? "text-emerald-400 bg-emerald-500/10" : h.sentiment.includes("Bear") ? "text-red-400 bg-red-500/10" : "text-zinc-400 bg-zinc-800/50"}`}>{h.sentiment}</span>
                    <span className="text-zinc-300">{h.title} <span className="text-zinc-600">· {h.source}</span></span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Active positions */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Active Positions</div>
            <div className="text-sm text-zinc-400">{activeSignals.length} of 2 max</div>
          </div>
          <div className="flex items-center gap-2">
            {activeSignals.length > 0 && Object.keys(marks).length > 0 && (
              <span data-testid="us-live-pnl" className={`text-xs font-mono px-2.5 py-1 rounded-full border ${totalUnrealized >= 0 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-red-500/10 border-red-500/30 text-red-400"}`}>
                Live P&L {totalUnrealized >= 0 ? "+" : ""}{usd(totalUnrealized)}
              </span>
            )}
            <button data-testid="us-pdf-btn" onClick={downloadPdf}
              className="text-xs px-3 py-1.5 rounded-lg border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-300 flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> PDF Log
            </button>
          </div>
        </div>
        <div className="space-y-3">
          <AnimatePresence>
            {activeSignals.length === 0 && (
              <div className="rounded-xl border border-zinc-900 bg-zinc-950/40 px-4 py-8 text-center text-sm text-zinc-500">
                {marketOpen ? "Scanning US tape for the next high-conviction setup…" : "US market closed. Engine resumes at 9:30 AM ET."}
              </div>
            )}
            {activeSignals.map((s) => {
              const m = marks[s.id];
              const isLong = s.direction === "LONG";
              const up = m && Number(m.unrealized_pnl) >= 0;
              return (
                <motion.div key={s.id} data-testid={`us-position-${s.id}`}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {isLong ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                      <div>
                        <div className="text-white font-semibold">
                          {s.ticker} <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ color: isLong ? "#00E676" : "#FF3B30", background: `${isLong ? "#00E676" : "#FF3B30"}15` }}>{s.direction}</span>
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.instrument_type}</span>
                          {s.sector && <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-800/60 text-zinc-400">{s.sector}</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">Conv {s.conviction} · {s.leverage}x · qty {s.qty} · hold {s.hold_minutes}m</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {m && (
                        <span data-testid={`us-unrealized-${s.id}`} className={`text-xs font-mono px-2 py-0.5 rounded-full border ${m.unrealized_pnl >= 0 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-red-500/10 border-red-500/30 text-red-400"}`}>
                          {m.unrealized_pnl >= 0 ? "+" : ""}{usd(m.unrealized_pnl)}
                        </span>
                      )}
                      <button data-testid={`us-close-${s.id}`} onClick={() => closeSig(s.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-zinc-500 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {m && (
                    <div data-testid={`us-live-${s.id}`} className="mt-3 grid grid-cols-3 gap-2 rounded-lg border border-zinc-900 p-2" style={{ background: up ? "rgba(0,230,118,0.06)" : "rgba(255,59,48,0.06)" }}>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LTP Live</div>
                        <div className="font-mono text-sm text-white mt-0.5">{usd(m.price)}</div>
                      </div>
                      <Field label="Unrealized" value={`${m.unrealized_pnl >= 0 ? "+" : ""}${usd(m.unrealized_pnl)}`} tone={up ? "up" : "down"} />
                      <Field label="Move" value={`${m.pnl_pct >= 0 ? "+" : ""}${m.pnl_pct}%`} tone={m.pnl_pct >= 0 ? "up" : "down"} />
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <Field label="Entry" value={usd(s.entry)} />
                    <Field label="Stop" value={usd(s.stop)} tone="down" />
                    <Field label="TP1 / TP2" value={`${usd(s.tp1)} / ${usd(s.tp2)}`} tone="up" />
                    <Field label="Max Loss" value={`-${usd(Math.abs(Number(s.max_loss_usd) || 0))}`} tone="down" />
                    <Field label="Notional" value={usd(s.notional_usd)} />
                    <Field label="R:R" value={`1:${s.rr_ratio || 2}`} />
                  </div>
                  {s.rationale_setup && (
                    <div className="mt-3 text-[11px] text-zinc-400 border-t border-zinc-900 pt-2">
                      <span className="font-mono uppercase text-[10px] tracking-widest text-zinc-500">Setup</span>: {s.rationale_setup}
                    </div>
                  )}
                  {s.rationale_news && (
                    <div className="mt-1.5 text-[11px] text-zinc-400">
                      <span className="font-mono uppercase text-[10px] tracking-widest text-[#F472B6]">News</span>: {s.rationale_news}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </section>

      {/* Recent Closed */}
      {closedSignals.length > 0 && (
        <section>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Recent Closed</div>
          <div className="rounded-xl border border-zinc-900 bg-zinc-950/40 divide-y divide-zinc-900">
            {closedSignals.map((s) => {
              const pnl = Number(s.realized_pnl) || 0;
              const isWin = pnl > 0;
              const Icon = isWin ? CheckCircle2 : s.outcome === "STOPPED" ? ShieldX : Target;
              return (
                <div key={s.id} data-testid={`us-closed-${s.id}`} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" style={{ color: isWin ? "#00E676" : s.outcome === "STOPPED" ? "#FF3B30" : "#A1A1AA" }} />
                    <span className="font-mono text-xs text-zinc-500">{(s.outcome || "").replace("_", " ")}</span>
                    <span className="text-white">{s.ticker} {s.direction}</span>
                    <span className="text-[11px] text-zinc-500">{usd(s.entry)} → {usd(s.close_price)}</span>
                  </div>
                  <div className={`font-mono text-sm ${pnl > 0 ? "text-emerald-400" : pnl < 0 ? "text-red-400" : "text-zinc-400"}`}>
                    {pnl >= 0 ? "+" : ""}{usd(pnl)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Watchlist */}
      {watch.length > 0 && (
        <section>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <Target className="w-3 h-3" /> Watchlist
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {watch.map((w, i) => (
              <div key={i} className="rounded-lg border border-zinc-900 bg-zinc-950/40 px-3 py-2 text-xs">
                <div className="text-white font-semibold">{w.ticker} {w.level ? `@ ${usd(w.level)}` : ""}</div>
                <div className="text-zinc-500 mt-0.5">{w.trigger}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Adaptive Lessons */}
      {lessons?.count > 0 && (
        <section>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <Brain className="w-3 h-3" /> Adaptive Learning ({lessons.count})
          </div>
          <div className="rounded-xl border border-zinc-900 bg-zinc-950/40 divide-y divide-zinc-900 max-h-56 overflow-y-auto">
            {lessons.lessons.slice(0, 20).map((l, i) => (
              <div key={i} className="px-4 py-2 text-xs">
                <span className={`font-mono ${l.outcome?.startsWith("WIN") ? "text-emerald-400" : l.outcome?.startsWith("LOSS") ? "text-red-400" : "text-zinc-400"}`}>{l.outcome}</span>
                <span className="text-zinc-400 ml-2">{l.ticker} {l.direction} · conv {l.conviction} · {usd(l.pnl)}</span>
                <div className="text-[11px] text-zinc-500 mt-0.5">{l.setup}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 9-Stage Adaptive Learning */}
      {active && <LearningPanel fetcher={usLearning} accent="#F472B6" />}

      {portfolio && (
        <section className="rounded-xl border border-zinc-900 bg-zinc-950/40 p-4">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            <Activity className="w-3 h-3" /> Portfolio Narrative · {portfolio.session_phase}
          </div>
          <div className="text-sm text-zinc-300">{portfolio.narrative}</div>
          <div className="text-xs text-zinc-500 mt-2">Next: {portfolio.next_action_hint} · Risk: {portfolio.risk_level}</div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-white";
  return (
    <div className="rounded-xl border border-zinc-900 bg-zinc-950/50 px-3 py-2.5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-lg mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function Field({ label, value, tone }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-zinc-200";
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-sm ${cls}`}>{value}</div>
    </div>
  );
}
